import type { FxDraftLayer, FxWorkspaceJob } from '@mobile-mapping/providers';
import { t } from '@trimble/i18n';
import type {
  TreeNodeDefinition,
  TreeNodeStatus,
} from '@trimble/ui-components/solidjs';
import { UnreachableCaseError } from 'ts-essentials';

import {
  breaklineTypeNodeId,
  breaklinesFolderId,
  draftLayerNodeId,
  jobItemId,
  pciFolderId,
} from '../selections/tree/fx-workspace-tree-nodes';

const BREAKLINES_SORT_PRIORITY = 0;
const PCI_SORT_PRIORITY = 1;
const CUSTOM_LAYER_SORT_PRIORITY = 2;
const JOB_SORT_PRIORITY = 3;
const FOLDER_ICON = 'tc-icon-layers';

export type DraftLayerTreeItem =
  | { readonly kind: 'breaklines' }
  | { readonly kind: 'pci' }
  | { readonly kind: 'layer'; readonly layer: FxDraftLayer }
  | { readonly kind: 'job'; readonly jobId: string };

function layerNodeId(workspaceId: string, layer: FxDraftLayer): string {
  if (layer.kind === 'breakline' && layer.breaklineType !== undefined) {
    return breaklineTypeNodeId(workspaceId, layer.breaklineType);
  }

  return draftLayerNodeId(workspaceId, layer.layerId);
}

function jobStatusMessage(status: FxWorkspaceJob['status']): string {
  switch (status) {
    case 'processing':
      return t('mobileMapping.fxWorkspace.jobStatus.processing');
    case 'complete':
      return t('mobileMapping.fxWorkspace.jobStatus.complete');
    case 'failed':
      return t('mobileMapping.fxWorkspace.jobStatus.failed');
    default:
      throw new UnreachableCaseError(status);
  }
}

function layerNode(
  workspaceId: string,
  layer: FxDraftLayer,
  parentId: string | undefined,
  sortPriority: number | undefined
): TreeNodeDefinition<DraftLayerTreeItem> {
  return {
    id: layerNodeId(workspaceId, layer),
    parentId,
    label: layer.label,
    isLeaf: true,
    sortPriority,
    dataTestId: `draft-layer-${layer.layerId}`,
    data: { kind: 'layer', layer },
  };
}

function folderNode(
  id: string,
  label: string,
  testId: string,
  sortPriority: number,
  kind: 'breaklines' | 'pci',
  status?: TreeNodeStatus
): TreeNodeDefinition<DraftLayerTreeItem> {
  return {
    id,
    label,
    labelIcon: FOLDER_ICON,
    isLeaf: false,
    showVisibilityToggle: false,
    sortPriority,
    dataTestId: testId,
    data: { kind },
    ...(status === undefined ? {} : { status }),
  };
}

function ensureNode(
  nodes: TreeNodeDefinition<DraftLayerTreeItem>[],
  node: TreeNodeDefinition<DraftLayerTreeItem>
): void {
  if (!nodes.some((existing) => existing.id === node.id)) {
    nodes.push(node);
  }
}

export function buildDraftLayerTreeNodes(
  workspaceId: string,
  layers: readonly FxDraftLayer[],
  jobs: readonly FxWorkspaceJob[],
  isBreaklinesBuilding = false
): TreeNodeDefinition<DraftLayerTreeItem>[] {
  const nodes: TreeNodeDefinition<DraftLayerTreeItem>[] = [];
  const breaklinesFolder = breaklinesFolderId(workspaceId);
  const pciFolder = pciFolderId(workspaceId);
  const breaklinesStatus: TreeNodeStatus | undefined = isBreaklinesBuilding
    ? {
        status: 'processing',
        message: t('mobileMapping.fxWorkspace.breaklines.building'),
      }
    : undefined;
  const breaklinesFolderNode = folderNode(
    breaklinesFolder,
    t('mobileMapping.fxWorkspace.breaklines.title'),
    'draft-layer-group-breakline',
    BREAKLINES_SORT_PRIORITY,
    'breaklines',
    breaklinesStatus
  );

  // Layers arrive after the create requests finish. Show the folder during
  // that wait so the Building subtitle stays visible.
  if (isBreaklinesBuilding) {
    ensureNode(nodes, breaklinesFolderNode);
  }

  for (const layer of layers) {
    switch (layer.kind) {
      case 'breakline':
        ensureNode(nodes, breaklinesFolderNode);
        nodes.push(layerNode(workspaceId, layer, breaklinesFolder, undefined));
        break;
      case 'pci':
        if (!nodes.some((node) => node.id === pciFolder)) {
          nodes.push(
            folderNode(
              pciFolder,
              t('mobileMapping.fxWorkspace.pci.title'),
              'draft-layer-group-pci',
              PCI_SORT_PRIORITY,
              'pci'
            )
          );
        }
        nodes.push(layerNode(workspaceId, layer, pciFolder, undefined));
        break;
      case 'custom':
        nodes.push(
          layerNode(workspaceId, layer, undefined, CUSTOM_LAYER_SORT_PRIORITY)
        );
        break;
      default:
        throw new UnreachableCaseError(layer.kind);
    }
  }

  for (const job of jobs) {
    nodes.push({
      id: jobItemId(workspaceId, job.id),
      label: job.name,
      isLeaf: true,
      sortPriority: JOB_SORT_PRIORITY,
      dataTestId: `draft-job-${job.id}`,
      data: { kind: 'job', jobId: job.id },
      // Tree status `failed` disables the row and hides the visibility toggle.
      status: { message: jobStatusMessage(job.status) },
    });
  }

  return nodes;
}
